<template>
  <div v-click-outside="handleClose" class="named-carts-consumer">
    <div
      v-if="savedCartsCustomer"
      class="named-carts-consumer__user"
      :class="{ 'named-carts-consumer__user--modal': isModalWindow }"
      :data-label="inputLabel"
    >
      <NamedCartsConsumerDetails :consumer="savedCartsCustomer" :has-header="false" />
      <input
        v-if="staticSelectionName"
        class="js-saved-carts-consumer-guid-input"
        type="hidden"
        :name="staticSelectionName"
        :value="savedCartsCustomer.guid"
      />
      <button
        :disabled="savedCartsIsCustomerDetaching"
        class="named-carts-consumer__remove"
        @click.prevent="handleDetach"
      >
        <Spinner v-if="savedCartsIsCustomerDetaching" size="xs" />
        <UseSvg v-else class="icon icon--svg icon--cross" icon="cross" />
      </button>
    </div>

    <template v-else>
      <NamedCartsField
        maxlength="50"
        :disabled="savedCartsIsCustomerAttaching"
        :is-modal-window="isModalWindow"
        :label="inputLabel"
        :placeholder="inputLabel"
        :readonly="isCustomerReadOnly"
        :value="consumerQuery"
        @focus="handleInputFocus"
        @input="handleInputChange"
      >
        <template #icon>
          <UseSvg
            class="named-carts-consumer__search-icon icon icon--svg icon--search"
            icon="search"
          />
        </template>
      </NamedCartsField>

      <div :class="listStyles" @click="handleClose">
        <div
          v-if="savedCartsIsCustomerChoosing"
          class="named-carts-consumer__items"
          @click.stop.prevent
        >
          <div class="named-carts-consumer__items-header">
            <h3 class="named-carts-consumer__items-header-title">Add A Customer</h3>

            <button
              aria-label="Close"
              class="named-carts-consumer__items-header-button"
              type="button"
              @click="handleClose"
            >
              <UseSvg aria-label="Close" icon="modal-close-big" />
            </button>
          </div>

          <div class="named-carts-consumer__action">
            <NamedCartsButton
              variant="primary"
              class="named-carts-consumer__new"
              @click="handleCreate"
            >
              Create A New Customer
            </NamedCartsButton>
          </div>

          <div v-if="loading || savedCartsIsCustomerAttaching" class="named-carts-consumer__loader">
            <Spinner />
            <span v-if="savedCartsIsCustomerAttaching">Attaching Customer</span>
          </div>

          <div v-else-if="!consumers.length" class="named-carts-consumer__placeholder">
            No customers found...
          </div>

          <template v-else>
            <div
              v-for="consumer in consumers"
              :key="consumer.id"
              class="named-carts-consumer__item"
              @click="handleSelect(consumer)"
            >
              <div
                :class="{
                  'named-carts-consumer__option': true,
                  'named-carts-consumer__option--active': consumer.active
                }"
              />
              <div class="named-carts-consumer__content">
                <div class="named-carts-consumer__name">
                  {{ consumer.firstName }} {{ consumer.lastName }}
                </div>
                <div v-if="consumer.companyName" class="named-carts-consumer__additional-info">
                  {{ consumer.companyName }}
                </div>
                <div class="named-carts-consumer__additional-info">
                  {{ consumer.city }}
                  <span v-if="consumer.stateName">, {{ consumer.stateName }}</span>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </template>
    <Teleport to="body">
      <NamedCartsAddConsumer ref="addConsumer" @consumer-created="handleSearch('')" />
    </Teleport>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import { objNamesToCamelCase } from "common/utils/helpers/cases";
import debounce from "lodash.debounce";
import { api } from "jiffy-api";
import { isTouchDevice } from "common/utils/browser";
import logger from "common/utils/logger";
import NamedCartsField from "./NamedCartsField.vue";
import NamedCartsButton from "./NamedCartsButton.vue";
import NamedCartsAddConsumer from "./NamedCartsAddConsumer.vue";
import NamedCartsConsumerDetails from "./NamedCartsConsumerDetails.vue";
import Spinner from "../Spinner.vue";
import UseSvg from "../UseSvg.vue";

const DEBOUNCE_INTERVAL = 500;

export default {
  name: "NamedCartsConsumer",
  components: {
    NamedCartsField,
    NamedCartsButton,
    NamedCartsAddConsumer,
    NamedCartsConsumerDetails,
    Spinner,
    UseSvg
  },
  props: {
    inputLabel: {
      type: String,
      default: "Add a Customer"
    },
    isModalWindow: {
      type: Boolean,
      default: false
    },
    staticSelectionName: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      consumerQuery: "",
      open: false,
      loading: false,
      consumers: []
    };
  },
  computed: {
    ...mapGetters("savedCarts", [
      "savedCartsCustomer",
      "savedCartsIsCustomerAttaching",
      "savedCartsIsCustomerDetaching",
      "savedCartsIsCustomerChoosing"
    ]),
    listStyles() {
      return {
        "named-carts-consumer__list": true,
        "named-carts-consumer__list--open":
          this.savedCartsIsCustomerChoosing && !this.isModalWindow,
        "named-carts-consumer__list--full-open":
          this.savedCartsIsCustomerChoosing && this.isModalWindow
      };
    },
    confirmInfo() {
      if (!this.savedCartsCustomer) return "";
      return `Are you sure you want to delete ${this.savedCartsCustomer.firstName} ${this.savedCartsCustomer.lastName}?`;
    },
    isCustomerReadOnly() {
      return isTouchDevice();
    }
  },
  watch: {
    consumerQuery: debounce(function f(value) {
      this.handleSearch(value);
    }, DEBOUNCE_INTERVAL)
  },
  mounted() {
    this.handleSearch("");

    if (this.isModalWindow) {
      const $modal = this.$el.closest(".modal");
      $($modal).on("shown.bs.modal", () => this.handleSearch(""));
    }
  },
  methods: {
    ...mapActions("savedCarts", ["attachCartCustomer", "detachCartCustomer"]),
    ...mapMutations("savedCarts", ["setSavedCartsCustomer", "setSavedCartsCustomerChoosing"]),
    handleCreate() {
      this.$refs.addConsumer.open();
      this.handleClose();
    },
    handleClose() {
      this.setSavedCartsCustomerChoosing(false);
    },
    handleInputChange(event) {
      this.consumerQuery = event.target.value;
      this.setSavedCartsCustomerChoosing(true);
    },
    handleInputFocus() {
      this.setSavedCartsCustomerChoosing(true);
    },
    async handleSearch(searchQuery) {
      try {
        this.loading = true;
        const { consumers } = await api.consumers.list({ consumer_query: searchQuery });
        this.consumers = objNamesToCamelCase(consumers);
      } catch (error) {
        logger.error(error);
      } finally {
        this.loading = false;
      }
    },
    handleSelect(consumer) {
      if (this.staticSelectionName) {
        this.setSavedCartsCustomer(consumer);
        this.handleClose();
        this.consumerQuery = "";
      } else {
        this.attachCartCustomer(consumer).then(() => {
          this.handleClose();
          this.consumerQuery = "";
        });
      }
    },
    handleDetach() {
      if (this.staticSelectionName) {
        this.setSavedCartsCustomer(null);
      } else {
        this.detachCartCustomer();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "frontend/init/jiffy/settings";
@import "frontend/init/jiffy/mixin";
@import "frontend/init/jiffy/button/mixin";

.named-carts-consumer {
  position: relative;

  &__list {
    &--full-open,
    &--open {
      display: flex;
      align-items: self-end;
      position: fixed;
      top: -1px;
      right: -1px;
      bottom: 0;
      left: -1px;
      background: rgba(0, 0, 0, 0.5);
      z-index: 999999;

      @include with-tablet {
        position: absolute;
        top: 100%;
        bottom: auto;
        margin-top: 4px;
        background: transparent;
      }
    }

    &--open {
      @include with-tablet {
        left: auto;
      }
    }
  }

  &__new {
    width: 100%;
  }

  &__items {
    position: relative;
    background: $white;
    overflow-y: auto;
    max-height: 50%;
    width: 100%;
    border-radius: 8px 8px 0 0;

    @include with-tablet {
      min-width: 376px;
      max-height: 280px;
      border-radius: 10px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
    }
  }

  &__items-header {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 48px;
    padding: 0 60px;
    border-bottom: 1px solid $gray-border;
    border-radius: 6px 6px 0 0;
    background-color: $section-grey;
    color: $text-black;

    @include with-tablet {
      display: none;
    }
  }

  &__items-header-title {
    margin: 11px 0;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.2;
    text-align: center;
    text-transform: capitalize;
  }

  &__items-header-button {
    @include clear-button;
    @include svg-wrapper;

    position: absolute;
    top: 0;
    right: 10px;
    width: 48px;
    height: 48px;
    padding: 15px;
  }

  &__action {
    padding: 32px 16px 16px;
  }

  &__name {
    font-weight: 700;
    font-size: 16px;
    line-height: 1.125;
    color: $text-black;
  }

  &__additional-info {
    margin-top: 2px;
    font-weight: 400;
    font-size: 12px;
    line-height: 1.33;
  }

  &__loader {
    margin: 16px 0 32px;
    display: flex;
    align-items: center;
    justify-content: center;

    div {
      width: 16px;
      margin-right: 8px;
    }
  }

  &__placeholder {
    margin: 16px 0 32px;
    text-align: center;
  }

  &__item {
    padding: 16px;
    cursor: pointer;
    display: flex;
    flex-direction: row;
    align-items: flex-start;

    & + & {
      border-top: 1px solid $gray-border;
    }
  }

  &__user {
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
    min-height: 52px;
    padding: 8px 12px 5px;
    border: 1px solid $gray-label;
    border-radius: 3px;

    &::before {
      content: attr(data-label);
      position: absolute;
      left: 8px;
      bottom: 100%;
      margin-bottom: -8px;
      padding: 0 4px;
      background: $white;
      color: $primary;
      font-size: 12px;
      line-height: 16px;
    }

    &--modal {
      padding-bottom: 8px;
      color: $gray-border;
    }
  }

  &__details {
    flex: 1;
    margin-right: 8px;
  }

  &__label {
    font-weight: 700;
    font-size: 12px;
    line-height: 1.16;
    text-transform: uppercase;
    color: $text-black;
  }

  &__info {
    font-weight: 400;
    font-size: 0.75rem;
    line-height: 1.43;
    color: $text-black;

    &--name {
      font-weight: 700;
      font-size: 0.875rem;
    }
  }

  &__remove {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border: none;
    padding: 0;
    border-radius: 50%;
    appearance: none;
    background-color: $light-gray;
    color: $gray-body;

    &:hover {
      background-color: darken($light-gray, 7);
    }

    & > svg {
      height: 12px;
      width: 12px;
    }
  }

  &__option {
    flex-shrink: 0;
    position: relative;
    border: 1px solid $gray-border;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    margin-right: 10px;

    &--active {
      background: $blue-primary;
      border-color: $blue-primary;

      &::before {
        position: absolute;
        content: "";
        top: 7px;
        left: 7px;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: white;
      }
    }
  }

  &__content {
    margin-left: 10px;
    flex: 1;
  }

  &__search-icon {
    display: block;
    height: 16px;
    width: 16px;
    color: $warm-grey;
  }
}
</style>
